<template>
  <div class="popup z-index-10-fg confirmation small-popup edit-diary-note-popup" ref="popupWindow">
    <div class="popup-content" ref="popupContent">
      <div class="popup-header">
        <h2>{{ isEdit ? 'Edit' : 'Add' }} Diary Note</h2>
        <img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('closePopup')" />
      </div>
      <div class="popup-body">
        <Dropdown v-if="showContactField" :options="contactOptions" label="Contact" v-model="contact" class="input-item medium-item" />

        <Dropdown :options="typeOptions" label="Type" v-model="type" class="input-item medium-item" :invalid="type === '' && showErrors" />

        <Dropdown :options="projectOptions" label="Project" v-model="project" class="input-item medium-item" :canBeEmpty="true" />

        <TextArea v-model="note" label="Note" :rows="6" :invalid="note === '' && showErrors" :required="true" class="input-item large-item" />
      </div>
      <div class="popup-footer">
        <button @click="$emit('closePopup')" class="text-btn">Close</button>
        <button :class="{ 'square-btn': true, saving: isSaving }" @click="saveDiaryNote()">Save<img src="../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import Dropdown from './inputs/Dropdown.vue';
import TextArea from './inputs/TextArea.vue';
import useAlerts from '@/composables/useAlerts';
import usePicklistItemsQuery from '@/composables/usePicklistItemsQuery';
import useCurrentUser from '@/composables/useCurrentUser';
import useDiaryMutate from '@/composables/useDiaryMutate';

export default {
  name: 'EditDiaryNote',
  emits: ['closePopup', 'save'],
  components: {
    Dropdown,
    TextArea,
  },
  props: {
    diaryEntry: {
      type: Object,
      default: null,
    },
    entityType: {
      type: String,
      required: true,
      validator: (value) => ['company', 'contact'].includes(value),
    },
    entityId: {
      type: String,
      required: true,
    },
    contacts: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { addAlert } = useAlerts();
    const { user } = useCurrentUser();
    const { createDiary, updateDiary } = useDiaryMutate();

    // Reactive data
    const note = ref('');
    const contact = ref('');
    const type = ref('');
    const project = ref('');
    const isSaving = ref(false);
    const showErrors = ref(false);

    // Computed properties
    const isEdit = computed(() => {
      return props.diaryEntry && props.diaryEntry.DIARYID;
    });

    const showContactField = computed(() => {
      return props.entityType === 'company';
    });

    const typeOptions = computed(() => {
      return usePicklistItemsQuery.pickLists.diaryTypes.items.map((item) => item.NAME);
    });

    const projectOptions = computed(() => {
      return usePicklistItemsQuery.pickLists.diaryProjects.items.map((item) => item.NAME);
    });

    const contactOptions = computed(() => {
      const options = [{ value: 'none', text: 'None (Company Only)' }];

      if (!props.contacts || props.contacts.length === 0) {
        return options;
      }

      props.contacts.forEach((contact) => {
        // Inactive contacts are only offered when the note already belongs to them
        if (contact.INACTIVE && contact.CONTACTID !== props.diaryEntry?.CONTACTID) return;

        const displayName = `${contact.FIRSTNAME || ''} ${contact.LASTNAME || ''}`.trim();
        if (displayName !== '') {
          options.push({
            value: contact.CONTACTID,
            text: (displayName || 'Unnamed Contact') + (contact.INACTIVE ? ' (Inactive)' : ''),
          });
        }
      });

      return options;
    });

    const canSave = computed(() => {
      const hasNote = note.value.trim() !== '';
      const hasType = type.value !== '';
      const hasContactIfRequired = !showContactField.value || (contact.value !== '' && contact.value !== null);

      return hasNote && hasType && hasContactIfRequired;
    });

    // Initialize form data
    const initializeForm = () => {
      if (props.diaryEntry) {
        // Editing existing entry
        note.value = props.diaryEntry.DIARYTEXT || '';
        type.value = props.diaryEntry.TYPE || '';
        project.value = props.diaryEntry.PROJECT || '';

        // Set contact if editing a company diary entry
        if (props.entityType === 'company') {
          contact.value = props.diaryEntry.contact ? props.diaryEntry.contact.CONTACTID : 'none';
        }
      } else {
        // Adding new entry - clear all fields
        note.value = '';
        contact.value = props.entityType === 'company' ? 'none' : '';
        type.value = '';
        project.value = '';
      }
    };

    // Save function
    const saveDiaryNote = async () => {
      if (!canSave.value) {
        showErrors.value = true;
        addAlert(`Please fill in all required fields`, 'high', 3);
        return;
      } else {
        showErrors.value = false;
      }

      isSaving.value = true;

      try {
        // Prepare the diary entry data
        const diaryEntryData = {
          DIARYTEXT: note.value.trim(),
          TYPE: type.value,
          PROJECT: project.value || '',
        };

        // Only include CREATEDATE and ASSIGNEDID if it's a new entry
        if (!isEdit.value) {
          diaryEntryData.CREATEDATE = Date.now().toString();
          diaryEntryData.ASSIGNEDID = user.userid;
        }

        // Add entity-specific fields
        if (props.entityType === 'company') {
          if (contact.value && contact.value !== 'none') {
            diaryEntryData.CONTACTID = contact.value;
            diaryEntryData.COMPANYID = null;
          } else {
            diaryEntryData.COMPANYID = props.entityId;
            diaryEntryData.CONTACTID = null;
          }
        } else if (props.entityType === 'contact') {
          diaryEntryData.CONTACTID = props.entityId;
          diaryEntryData.COMPANYID = null;
        }

        // Call appropriate mutation
        if (isEdit.value) {
          await updateDiary(props.diaryEntry.DIARYID, diaryEntryData);
        } else {
          await createDiary(diaryEntryData);
        }

        // Show success message
        addAlert(isEdit.value ? 'Diary note updated successfully' : 'Diary note added successfully', 'low', 2);

        // Emit save event for parent component to refresh data
        emit('save');

        // Close the popup
        emit('closePopup');
      } catch (error) {
        console.error('Error saving diary note:', error);
        addAlert('Error saving diary note', 'high', 5);
      } finally {
        isSaving.value = false;
      }
    };

    // Watch for diary entry changes to reinitialize form
    watch(() => props.diaryEntry, initializeForm, { immediate: true });

    return {
      note,
      contact,
      type,
      project,
      isSaving,
      isEdit,
      showContactField,
      typeOptions,
      projectOptions,
      contactOptions,
      canSave,
      saveDiaryNote,
      addAlert,
      showErrors,
    };
  },
};
</script>

<style lang="scss" scoped>
.edit-diary-note-popup.small-popup {
  width: 50rem;

  .popup-body {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1.5rem 1rem;
    margin-bottom: 1.5rem;

    .input-item {
      min-width: 20rem;
      flex: 1 0 45%;

      &.small-item {
        min-width: 5rem;
        flex: 1 0 10%;
      }

      &.large-item {
        min-width: 20rem;
        flex: 1 0 90%;
      }

      &.medium-item {
        min-width: 10rem;
        flex: 1 0 30%;
      }
    }
  }
}
</style>
